import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Menu, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useShop } from "@/context/ShopContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "New Originals", to: "/new-originals", id: "new" },
  { label: "Women", to: "/women", id: "women", sub: [["Denim", "/women-denim"], ["Graphic Tees", "/women-tees"], ["Dresses", "/women-dresses"], ["Hoodies & Sweatshirts", "/women-fleece"]] },
  { label: "Men", to: "/men", id: "men", sub: [["Denim", "/men-denim"], ["Graphic Tees", "/men-tees"], ["Basic Tees", "/men-basic-tees"], ["Shirts", "/men-shirts"], ["Hoodies & Sweatshirts", "/men-fleece"]] },
  { label: "About", to: "/about", id: "about" },
  { label: "Wholesale", to: "/wholesale", id: "wholesale" },
];

export const Header = () => {
  const { setSearchOpen } = useShop();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-40 bg-white transition-all duration-300 ${scrolled ? "border-b border-border shadow-[0_1px_20px_rgba(15,30,54,0.04)]" : "border-b border-border/40"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="h-16 lg:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="lg:hidden p-1.5 -ml-1.5" aria-label="Open menu">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[86vw] max-w-sm p-0 bg-white overflow-y-auto">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Browse AR / American Rag collections.</SheetDescription>
                <div className="flex items-center px-5 h-16 border-b"><Logo className="text-base" /></div>
                <nav className="flex flex-col py-2">
                  {NAV.map((n) => (
                    <div key={n.id} className="border-b border-border/60">
                      <Link to={n.to} data-testid={`mobile-nav-${n.id}`} className="block px-5 py-4 text-sm font-heading font-bold uppercase tracking-wide text-ink">
                        {n.label}
                      </Link>
                      {n.sub && (
                        <div className="pb-3 -mt-1">
                          {n.sub.map(([l, to]) => (
                            <Link key={to} to={to} data-testid={`mobile-nav${to.replace(/\//g, "-")}`} className="block pl-8 pr-5 py-2 text-[13px] text-muted-foreground hover:text-ink">
                              {l}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
            <Logo className="text-base lg:text-lg" />
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <div key={n.id} className="relative group">
                <Link to={n.to} data-testid={`nav-${n.id}`} className="relative inline-flex items-center gap-1 text-[12px] font-heading font-semibold uppercase tracking-[0.1em] py-1 text-ink/70 hover:text-ink transition-colors">
                  {n.label}
                  {n.sub && <ChevronDown className="w-3 h-3 opacity-50 group-hover:rotate-180 transition-transform" />}
                  <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-navy group-hover:w-full transition-all duration-300" />
                </Link>
                {n.sub && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-white border border-border shadow-[0_18px_50px_rgba(15,30,54,0.12)] min-w-[220px] py-2">
                      {n.sub.map(([l, to]) => (
                        <Link key={to} to={to} data-testid={`nav${to.replace(/\//g, "-")}`} className="block px-5 py-3 text-[13px] font-medium text-ink/75 hover:text-ink hover:bg-offwhite transition-colors">
                          {l}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <button data-testid="search-button" onClick={() => setSearchOpen(true)} aria-label="Search" className="p-2 hover:text-navy transition-colors">
            <Search className="w-5 h-5" />
          </button>
        </div>
      </div>
    </header>
  );
};
